import * as React from 'react';
// the app-import-free slice of `src/i18n` (see i18n-context.tsx): same translator as the rest of the app,
// without pulling auth / api / layout into the design system's module graph
import { useTranslations } from 'i18n/use-translations';
import { DataTableLabels } from './types';

/**
 * Applies `:token` placeholders the way the app's translator does, so an override behaves like a
 * translation. The FUNCTION form of `replace`, because token values are customer data and a string
 * replacement would interpret `$&`, `$'` and `$1` inside them as patterns. Whitespace collapses so a
 * token that resolves empty does not leave a double space behind.
 */
export const formatLabel = (template: string, tokens?: Record<string, string>): string => {
    if (!tokens) {
        return template;
    }

    return Object.keys(tokens)
        .reduce((acc, token) => acc.replace(new RegExp(`:${token}`, 'ig'), () => tokens[token]), template)
        .replace(/\s+/g, ' ')
        .trim();
};

const DataTableLabelsContext = React.createContext<DataTableLabels | undefined>(undefined);

/**
 * Publishes resolved labels to everything in the table, so `selectionColumn()` honours the `labels` prop
 * without it being threaded through TanStack's column defs.
 */
export const DataTableLabelsProvider = DataTableLabelsContext.Provider;

/**
 * Three tiers, narrowest first: a per-table `labels` prop, an ancestor's provider, then the app translator.
 * The translator itself echoes the key on a miss, so a missing string is visible and greppable rather than
 * a blank. Storybook and the unit-test wrapper mount an English-backed `I18nContext`, so real copy renders
 * there too.
 *
 * `inherit: false` drops the middle tier, and a `DataTable` always passes it. The provider republishes a
 * FULLY resolved set, so without this a table nested in another one's cell would adopt the outer table's
 * strings. Cells and column factories still inherit, which is the whole point of the provider.
 */
export const useDataTableLabels = (
    overrides?: Partial<DataTableLabels>,
    options?: { inherit?: boolean },
): DataTableLabels => {
    const { __ } = useTranslations();
    const inherited = React.useContext(DataTableLabelsContext);
    const ancestor = options?.inherit === false ? undefined : inherited;

    const resolve = (key: keyof DataTableLabels, translationKey: string): string =>
        overrides?.[key] ?? ancestor?.[key] ?? __(translationKey);

    return {
        noData: resolve('noData', 'data_table.no_data'),
        noResults: resolve('noResults', 'data_table.no_results'),
        loading: resolve('loading', 'data_table.loading'),
        loadingMore: resolve('loadingMore', 'data_table.loading_more'),
        selectRow: resolve('selectRow', 'data_table.select_row'),
        selectAllRows: resolve('selectAllRows', 'data_table.select_all_rows'),
        sortAscending: resolve('sortAscending', 'data_table.sort_ascending'),
        sortDescending: resolve('sortDescending', 'data_table.sort_descending'),
        sortClear: resolve('sortClear', 'data_table.sort_clear'),
        sortedAscending: resolve('sortedAscending', 'data_table.sorted_ascending'),
        sortedDescending: resolve('sortedDescending', 'data_table.sorted_descending'),
        sortingCleared: resolve('sortingCleared', 'data_table.sorting_cleared'),
        filterActive: resolve('filterActive', 'data_table.filter_active'),
        expandRow: resolve('expandRow', 'data_table.expand_row'),
        rowDetail: resolve('rowDetail', 'data_table.row_detail'),
        expandColumnHeader: resolve('expandColumnHeader', 'data_table.expand_column_header'),
    };
};
